import React, { useEffect, useState } from 'react';
import { Drawer, Spin } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import type { ClaimDetailVo, TBillingApiScope } from 'types/insuranceBilling';
import { getBillingClaimDetail } from 'api/insuranceBilling';
import { formatDateMDY, formatMoney, formatTimestampMDY } from './statusConfig';
import IssueBlock from './IssueBlock';
import FixActions, { hasActionTarget } from './FixActions';
import StatusBadge from './StatusBadge';
import s from './s.module.less';

// timeline event code → 展示文案（timeline.event 为事件码，未知值原样显示）
// 前几项与原型 timeline() 同文案；PENDING / RETURNED / CANCELED / ARCHIVED / PATIENT_RESPONSIBLE
// 是 dev 实测出现、原型没有的事件码，按对应状态名拟的（TODO(PM): 待确认）
const TIMELINE_EVENT_TEXT: Record<string, string> = {
    CREATED: 'Claim created',
    SUBMITTED: 'Submitted to clearinghouse',
    ACKNOWLEDGED: 'Acknowledged by payer',
    PENDING: 'In review with payer',
    PAYMENT_POSTED: 'Payment posted',
    PAID: 'Payment processed',
    PARTIAL: 'Partial payment received',
    PATIENT_RESPONSIBLE: 'Processed, billed to the patient',
    REJECTED: 'Claim rejected',
    RETURNED: 'Claim returned',
    DENIED: 'Claim denied',
    CANCELED: 'Claim cancelled',
    ARCHIVED: 'Claim closed',
};

type Props = {
    claimId: number | null;
    apiScope: TBillingApiScope;
    onClose: () => void;
};

const ClaimDrawer = ({ claimId, apiScope, onClose }: Props) => {
    const [detail, setDetail] = useState<ClaimDetailVo | null>(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        // 换目标时先清掉上一笔的数据，失败也不能继续显示旧详情
        setDetail(null);
        setFailed(false);
        if (claimId == null) return undefined;
        let cancelled = false;
        setLoading(true);
        (async () => {
            const res: any = await getBillingClaimDetail(claimId, apiScope);
            if (cancelled) return;
            const vo: ClaimDetailVo | null = (!res?.error && res?.data?.data) || null;
            setDetail(vo);
            setFailed(!vo);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [claimId, apiScope]);

    const fields = detail
        ? [
              { label: 'Patient', value: detail.patient },
              { label: 'Payer', value: detail.payer || '—' },
              { label: 'Date of service', value: formatDateMDY(detail.dos) },
              { label: 'Billed', value: formatMoney(detail.billed) },
              { label: 'Paid', value: formatMoney(detail.paid) },
              { label: 'Handled by', value: detail.handledBy === 'manual' ? 'Manual' : 'Klarity' },
              { label: 'Source EHR', value: detail.source || '—' },
          ]
        : [];

    // 已付但保险实付 $0：payer 核准后全额计入患者未满足的 deductible。患者应付额直接用
    // 服务端 payment.patientOwes（不能用 billed - paid 推算）；旧提交的回单不拿来解释当前这次
    const payment = detail?.payment;
    const isPaidZero =
        (detail?.state.key === 'paid' || detail?.state.key === 'patient_responsibility') &&
        (detail.paid ?? 0) === 0 &&
        !!payment &&
        !payment.forEarlierSubmission &&
        payment.patientOwes > 0;

    // denied / rejected 的 issue block（原型 09/10）。一律以「Klarity is already working this one」
    // 开场；分类（coverage / general）、原因与建议都由服务端 denial 给出，只有服务端给了
    // denialAction 且目标有效时才出按钮（coverage denial 的 Message patient / Open in EHR）
    const denial = detail?.denial;
    // 问题块按 state 决定是哪种事，不能只看有没有 denial：资格检查没过而被 Klarity 扣着的 claim
    // （原始 NO_ELIGIBILITY → state not_ready）也带 denial，但它根本没发给 payer
    const rejected = detail?.state.key === 'rejected';
    const returned = rejected || detail?.state.key === 'denied';
    const payerName = detail?.payer || 'the payer';
    // 原型只有三种标签：Can't be billed（琥珀，发不出去、等 provider 处理）/ Claim denied / Claim rejected。
    // 资格检查没过的 claim（state not_ready，与卡住的 visit 同一状态）原型没单独设计，归到 Can't be billed。
    // 标题句照原型加粗「{patient}'s claim」与 payer，并写出具体原因。
    // TODO(PM): Can't be billed 那句是前端拟的（原型未覆盖此场景），待确认
    const reason = denial?.label || '';
    // 原因接在冒号后面，首字母转小写；像 "NPI ..." 这种缩写开头的保留原样
    const reasonInline = /^[A-Z][a-z]/.test(reason) ? reason.charAt(0).toLowerCase() + reason.slice(1) : reason;
    let issueChip = "Can't be billed";
    let issueLine: React.ReactNode = (
        <>
            <b>{detail?.patient}&apos;s claim</b> can&apos;t be sent to <b>{payerName}</b>
            {reasonInline ? `: ${reasonInline}.` : '.'}
        </>
    );
    if (returned) {
        issueChip = rejected ? 'Claim rejected' : 'Claim denied';
        issueLine = (
            <>
                <b>{detail?.patient}&apos;s claim</b> was {rejected ? 'rejected before reaching' : 'denied by'}{' '}
                <b>{payerName}</b>.
            </>
        );
    }
    const denialActions = detail?.denialAction ? [detail.denialAction] : [];
    const hasDenialAction =
        !!detail && denialActions.some((a) => hasActionTarget(a, detail.patientRef, detail.visitRef));

    const validationIcon = (status: string) => {
        if (status === 'pass') return <CheckCircleOutlined className={s.valPass} />;
        if (status === 'block') return <CloseCircleOutlined className={s.valBlock} />;
        return <WarningOutlined className={s.valWarn} />;
    };

    return (
        <Drawer
            open={claimId != null}
            onClose={onClose}
            width={440}
            title={
                detail ? (
                    <div className={s.drawerHead}>
                        <span className={s.drawerTitle}>Claim #{detail.id}</span>
                        <StatusBadge state={detail.state} />
                    </div>
                ) : (
                    'Claim'
                )
            }
        >
            {loading && (
                <div className={s.drawerLoading}>
                    <Spin />
                </div>
            )}
            {!loading && failed && <div className={s.issueText}>We couldn&apos;t load this claim. Try again.</div>}
            {/* 原型顺序：问题块在最上面，字段在下面 */}
            {!loading && detail && (
                <div className={s.drawerStack}>
                    {denial && (
                        // payer 退回的（denied / rejected）是 Klarity 在跟：红色，以「already working」开场；
                        // 还没发出去、等 provider 处理的（如资格检查没过）：琥珀色，与卡住的 visit 同一语义
                        <IssueBlock
                            tone={returned ? 'red' : 'amber'}
                            chip={issueChip}
                            issue={issueLine}
                            causes={<div className={s.issueText}>{denial.cause}</div>}
                            recommendation={
                                returned ? (
                                    <>
                                        <b>Klarity is already working this one.</b> {denial.recommendation}
                                    </>
                                ) : (
                                    denial.recommendation
                                )
                            }
                            actions={
                                hasDenialAction && (
                                    <FixActions
                                        actions={denialActions}
                                        patient={detail.patient}
                                        patientRef={detail.patientRef}
                                        visitRef={detail.visitRef}
                                    />
                                )
                            }
                            // 求助行只给 provider 自己要修的（与卡住的 visit 一致）；payer 退回的是 Klarity 在跟，不给
                            support={
                                !returned && (
                                    <>
                                        Not sure how to fix it? Email us at{' '}
                                        <a href="mailto:payer@helloklarity.com">payer@helloklarity.com</a>.
                                    </>
                                )
                            }
                        />
                    )}

                    <div className={s.drawerGrid}>
                        {fields.map((f) => (
                            <div key={f.label}>
                                <div className={s.drawerLabel}>{f.label}</div>
                                <div className={s.drawerValue}>{f.value}</div>
                            </div>
                        ))}
                    </div>

                    {isPaidZero && (
                        <div className={s.paidZeroBlock}>
                            <div className={s.paidZeroTitle}>Why this paid claim shows $0</div>
                            This {formatMoney(payment?.patientOwes)} is the patient&apos;s responsibility. The payer
                            approved the claim but applied it to the patient&apos;s unmet deductible, so insurance pays
                            $0. Nothing for you to fix.
                        </div>
                    )}

                    {!!detail.timeline?.length && (
                        <>
                            <div className={s.drawerSec}>Timeline</div>
                            <div>
                                {detail.timeline.map((t) => (
                                    <div key={`${t.date}-${t.event}`} className={s.tlRow}>
                                        <span className={s.tlDot} />
                                        <div>
                                            <div className={s.tlEvent}>{TIMELINE_EVENT_TEXT[t.event] || t.event}</div>
                                            <div className={s.tlDate}>{formatTimestampMDY(t.date)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {!!detail.validation?.length && (
                        <>
                            <div className={s.drawerSec}>Validation</div>
                            {detail.validation.map((v) => (
                                <div key={v.label} className={s.valRow}>
                                    {validationIcon(v.status)}
                                    <span>
                                        {v.label}
                                        {v.note ? ` · ${v.note}` : ''}
                                    </span>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </Drawer>
    );
};

export default ClaimDrawer;
